import type {
    ChangeEvent,
    ComponentProps,
    HTMLInputTypeAttribute,
    HTMLProps,
} from 'react';
import { useCallback, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import {
    FormInputContainer,
    StyledInput,
    StyledLabel,
    StyledError,
    StyleldButton,
} from './styled';

import { Icon, IconName } from '../Icon';

type IFormInputProps = ComponentProps<typeof FormInputContainer> & {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, react/require-default-props
    onChange?: (e: any) => void;
    label?: string;
    type?: HTMLInputTypeAttribute | 'textarea';
    placeholder?: string;
    disabled?: boolean;
    disabledValue?: string;
    min?: number;
    max?: number;
};

/**
 * In order to work properly, this component needs to be in some FormProvider
 */
export function FormInput({
    id,
    label,
    type,
    placeholder,
    disabled,
    disabledValue,
    onChange,
    min,
    max,
    ...props
}: IFormInputProps) {
    const [isPasswordShown, setPasswordShown] = useState(false);
    const { register } = useFormContext();
    const {
        fieldState: { error },
    } = useController({ name: id });
    const isPasswordInput = type === 'password';
    const inputType = isPasswordInput && isPasswordShown ? 'text' : type;

    const renderShowPassword = () => (
        <StyleldButton
            onClick={(event) => {
                event.preventDefault();
                setPasswordShown((prevState) => !prevState);
            }}
        >
            {isPasswordShown ? (
                <Icon iconName={IconName.HIDE_PASSWORD} />
            ) : (
                <Icon iconName={IconName.SHOW_PASSWORD} />
            )}
        </StyleldButton>
    );

    const value: HTMLProps<HTMLInputElement> =
        disabled && disabledValue
            ? {
                  value: disabledValue,
              }
            : {};

    const recalculateHeight = useCallback((element: HTMLInputElement) => {
        element.style.height = 'auto';
        const height = element.scrollHeight;
        element.style.height = `${height + 2}px`;
    }, []);

    const isTextarea = inputType === 'textarea';

    return (
        <FormInputContainer {...props}>
            <StyledLabel htmlFor={id} disabled={disabled}>
                {label}
            </StyledLabel>
            <StyledInput
                {...register(id)}
                onChange={onChange}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                as={(isTextarea ? 'textarea' : undefined) as any}
                type={!isTextarea ? inputType : undefined}
                hasError={Boolean(error)}
                disabled={disabled}
                placeholder={placeholder}
                onInput={
                    isTextarea
                        ? (event: ChangeEvent<HTMLInputElement>) =>
                              recalculateHeight(event.currentTarget)
                        : undefined
                }
                min={min}
                max={max}
                {...value}
            />
            {isPasswordInput && renderShowPassword()}
            <StyledError>{error?.message}</StyledError>
        </FormInputContainer>
    );
}

FormInput.defaultProps = {
    type: 'text',
    label: undefined,
    defaultValue: undefined,
    placeholder: undefined,
    disabled: false,
    disabledValue: undefined,
    min: undefined,
    max: undefined,
};
